import { catalogMatchConfidence, SEARCH_MISS_CONFIDENCE_THRESHOLD, normalizeCatalogSearchText } from './catalogSearchConfidence.js'
import { expandSearchQuery } from './searchQueryAliases.js'
import { buildSearchHierarchy } from './catalogSearchHierarchy.js'

export function queryText(query) {
  return normalizeCatalogSearchText(expandSearchQuery(query))
}

export function usableCardImage(row) {
  if (!row || row.image_flagged === true) return null
  const url = String(row.card_image || row.rep_card_image || '').trim()
  return url || null
}

export function rowHasUsableCardImage(row) {
  return Boolean(usableCardImage(row))
}

export function demoteBlankImageItems(items = [], getRow = item => item) {
  const imaged = []
  const blank = []
  for (const item of items) {
    (rowHasUsableCardImage(getRow(item)) ? imaged : blank).push(item)
  }
  return [...imaged, ...blank]
}

export function lineRepIsPlaceholder(line) {
  return !rowHasUsableCardImage({
    card_image: line?.rep_card_image ?? line?.card_image,
    image_flagged: line?.image_flagged,
  })
}

// Same order as fos_search_lines_v1 after FOS-SEARCH-BLANK-LINES-001:
// visible image first, then rank / size / name. Blank lines stay in the
// list so a thin house is still complete; they just leave the top window.
export function rankLiveLinesImagedFirst(lines = []) {
  return [...lines].sort((a, b) => {
    const aImg = !lineRepIsPlaceholder(a)
    const bImg = !lineRepIsPlaceholder(b)
    if (aImg !== bImg) return aImg ? -1 : 1
    const rank = (Number(b.rank) || 0) - (Number(a.rank) || 0)
    if (rank) return rank
    const variants = (Number(b.variant_count) || 0) - (Number(a.variant_count) || 0)
    if (variants) return variants
    const house = String(a.house || '').localeCompare(String(b.house || ''))
    if (house) return house
    return String(a.line_label || a.line_key || '').localeCompare(String(b.line_label || b.line_key || ''))
  })
}

export function houseNameMatchesQuery(house, normalizedQuery) {
  const h = normalizeCatalogSearchText(house)
  const q = normalizeCatalogSearchText(normalizedQuery)
  if (!h || !q || q.length < 3) return false
  if (h === q) return true
  return q.length >= 4 && h.startsWith(q)
}

function queryIsHouseOnly(normalizedQuery, house) {
  const h = normalizeCatalogSearchText(house)
  const q = normalizeCatalogSearchText(normalizedQuery)
  if (!h || !q) return false
  if (h === q) return true
  return q.length >= 4 && h.startsWith(q)
}

export function lineMatchRank(line, normalizedQuery) {
  const q = normalizeCatalogSearchText(normalizedQuery)
  const label = normalizeCatalogSearchText(line?.line_label || line?.name || '')
  const key = normalizeCatalogSearchText(line?.line_key || '')
  if (!q) return 0
  if (label === q || key === q) return 3
  if ((label && label.startsWith(q)) || (key && key.startsWith(q))) return 2
  if (label.length >= 4 && (q.startsWith(`${label} `) || ` ${q} `.includes(` ${label} `))) return 1
  if (key.length >= 4 && (q.startsWith(`${key} `) || ` ${q} `.includes(` ${key} `))) return 1
  return 0
}

function uniqueHouses(values) {
  const seen = new Set()
  const houses = []
  for (const value of values) {
    const house = String(value || '').trim()
    const key = normalizeCatalogSearchText(house)
    if (!house || seen.has(key)) continue
    seen.add(key)
    houses.push(house)
  }
  return houses
}

function dominantHouseForQuery(normalizedQuery, rows = []) {
  if (!rows.length) return null
  const matches = rows.filter(row => houseNameMatchesQuery(row.house, normalizedQuery))
  if (matches.length < Math.max(1, Math.ceil(rows.length * 0.55))) return null
  const houses = uniqueHouses(matches.map(row => row.house))
  return houses.length === 1 ? houses[0] : null
}

function matchingLiveLines(lines, normalizedQuery) {
  const scored = lines
    .map(line => ({ line, rank: lineMatchRank(line, normalizedQuery) }))
    .filter(entry => entry.rank > 0)
  const strong = scored.filter(entry => entry.rank >= 2)
  const chosen = strong.length ? strong : scored
  return chosen.map(entry => entry.line)
}

export function liveLineHouses(lines = []) {
  const houses = new Map()
  for (const line of lines) {
    const house = String(line.house || '').trim() || 'Unknown'
    if (!houses.has(house)) houses.set(house, [])
    houses.get(house).push(line)
  }
  return [...houses.entries()].map(([house, houseLines]) => ({ house, lines: houseLines }))
}

function demoteDerivedHierarchy(houses = []) {
  return houses.map(house => ({
    ...house,
    lines: demoteBlankImageItems(
      (house.lines || []).map(line => ({
        ...line,
        variants: demoteBlankImageItems(line.variants || []),
        fragrances: demoteBlankImageItems(
          (line.fragrances || []).map(fragrance => ({
            ...fragrance,
            variants: demoteBlankImageItems(fragrance.variants || []),
          })),
          fragrance => (fragrance.variants || []).find(rowHasUsableCardImage) || fragrance.variants?.[0],
        ),
      })),
      line => (line.variants || []).find(rowHasUsableCardImage) || line.variants?.[0],
    ),
  }))
}

function derivedHousesForLead(lead, house, rows, preferredRow = null) {
  let derived = demoteDerivedHierarchy(buildSearchHierarchy(rows))
  // The images-first rule governs house browse. A specific, already-ranked
  // bottle hunt must not bury its exact containing line behind unrelated photos.
  if (preferredRow?.id && rows.filter(row => row.id === preferredRow.id).length === 1) {
    derived = derived.map(entry => {
      if (normalizeCatalogSearchText(entry.house) !== normalizeCatalogSearchText(preferredRow.house)) return entry
      const containsTarget = line => line.variants.some(row => row.id === preferredRow.id)
      return { ...entry, lines: [...entry.lines.filter(containsTarget), ...entry.lines.filter(line => !containsTarget(line))] }
    })
  }
  if (lead !== 'house' || !house) return derived
  const houseKey = normalizeCatalogSearchText(house)
  return derived.filter(entry => normalizeCatalogSearchText(entry.house) === houseKey)
}

export function detectCatalogSearchLead(query, { rows = [], lines = [] } = {}) {
  const q = queryText(query)
  const liveLines = Array.isArray(lines) ? lines : []
  if (!q) {
    return { lead: 'none', house: null, displayLines: [] }
  }

  const lineHits = matchingLiveLines(liveLines, q)
  const housesFromLines = uniqueHouses(
    liveLines.map(line => line.house).filter(house => houseNameMatchesQuery(house, q)),
  )
  const houseFromRows = dominantHouseForQuery(q, rows)
  const house = housesFromLines[0] || houseFromRows
  const houseOnly = house && queryIsHouseOnly(q, house)

  if (houseOnly && (!lineHits.length || lineHits.every(line => queryIsHouseOnly(q, line.house)))) {
    const houseLines = liveLines.filter(
      line => normalizeCatalogSearchText(line.house) === normalizeCatalogSearchText(house),
    )
    return { lead: 'house', house, displayLines: houseLines }
  }

  if (lineHits.length) {
    return {
      lead: 'line',
      house: lineHits[0].house || house,
      displayLines: lineHits,
    }
  }

  if (liveLines.length) {
    return { lead: 'line', house: house || liveLines[0].house, displayLines: liveLines }
  }

  if (houseOnly) {
    return { lead: 'house', house, displayLines: [] }
  }

  return { lead: 'derived', house: house || null, displayLines: [] }
}

// A weaker line-name substring must not hide the already-ranked bottle match.
// This affects presentation only: no new identities or concentration claims.
function liveLinesDisplaceStrongRow(query, rows, detected) {
  const row = rows[0]
  if (!row || detected.lead === 'house' || !detected.displayLines.length) return false
  const q = queryText(query)
  const house = normalizeCatalogSearchText(row.house)
  const name = normalizeCatalogSearchText(row.name)
  if (!house || !name || queryIsHouseOnly(q, house)) return false
  const contains = (text, phrase) => ` ${text} `.includes(` ${phrase} `)
  const explicitHouseName = contains(q, house) && contains(q, name) && q !== house
  const strong = catalogMatchConfidence(q, row).confidence >= SEARCH_MISS_CONFIDENCE_THRESHOLD
  if (!strong && !explicitHouseName) return false
  // A specific full-query line is positive evidence even when the row service
  // returned a broader sibling. Keep that line rather than inferring identity.
  if (detected.displayLines.some(line => lineMatchRank(line, q) >= 2)) return false
  const coversRow = line => normalizeCatalogSearchText(line.house) === house
    && ((line.rep_id || line.rep_catalog_id) === row.id || [line.line_label, line.line_key].some(value => {
      const label = normalizeCatalogSearchText(value)
      return label && (contains(name, label) || contains(label, name))
    }))
  return !detected.displayLines.some(coversRow)
    || normalizeCatalogSearchText(detected.displayLines[0].house) !== house
}

export function resolveCatalogSearchPresentation(query, rows = [], lines = []) {
  let detected = detectCatalogSearchLead(query, { rows, lines })
  const preserveTargetLine = liveLinesDisplaceStrongRow(query, rows, detected)
  if (preserveTargetLine) {
    detected = { lead: 'derived', house: rows[0].house, displayLines: [] }
  }
  // Stable partition only. The RPC owns rank/size/name order among imaged
  // lines; Fast Add just lifts photographed reps above blanks.
  const displayLines = demoteBlankImageItems(
    detected.displayLines,
    line => ({ card_image: line.rep_card_image, image_flagged: line.image_flagged }),
  )
  const useLiveLines = displayLines.length > 0
  let houses
  if (useLiveLines) {
    houses = liveLineHouses(displayLines)
  } else {
    houses = derivedHousesForLead(detected.lead, detected.house, rows, preserveTargetLine ? rows[0] : null)
  }
  return {
    ...detected,
    displayLines,
    useLiveLines,
    houses,
  }
}
